import React, { useState } from 'react';
import { Button, createStyles, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Grid, makeStyles, Popper, PopperProps, TextField, Theme } from "@material-ui/core";
import { mainTheme } from '../../Theme';
import { Field, Form } from 'react-final-form';
import { EditorWrapper } from '../../components/Editor';
import { EmailTemplateMessageTypeEnum, EmailTemplateTypeEnum, MessageTypeEnum } from '../../emailTemplatesConfiguration/EmailTemplateRespository';
import { DropzoneArea } from 'material-ui-dropzone';
import { DocumentFile } from '../../types/DocumentFile';
import { normalizeFileName, retrieveQueryData } from '../../helpers/commonHelper';
import { useApplicationSettings } from '../../applicationSettings/ApplicationSettingsRepository';
import gql from 'graphql-tag';
import { showNotification } from '../../App';
import { EmailTemplateSelector } from '../../enquirySummary/selectors/EmailTemplateSelector';
import { IAutoCompleteItem } from '../../typings/autoComplete';
import { FormApi } from 'final-form';
import { client } from '../..';
import { RvLoader } from '../../components/Loader';
import DialogBox from '../../Dashboard/Component/DialogBox';

interface MatterSendEmailDialogProps extends Pick<DialogProps, 'open' | 'onClose' | 'maxWidth' | 'fullWidth'> {
    entityGuid: string;
}

interface NameSendEmailFormValues extends Omit<TemplateWithEmailBody, 'id' | 'emailType' | 'emailStyle'> {
    emailTemplate?: IAutoCompleteItem | null;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
        },
        textField: {
            width: '100%',
        },
        commendField: {
            width: '100%',
        },
        dialogTitle: {
            color: mainTheme.TemplateColor.Primary,
        },
        dropzone: {
            paddingTop: 10,
            paddingBottom: 10,
        },
        matterLabel: {
            display: 'flex',
            alignItems: 'center',
            height: '100%',
        },
        hideDate: {
            display: 'none'
        },
        divider: {
            width: '100%',
        },
        radioIcon: {
            color: mainTheme.BrandColors.TemplatePrimary
        },
        radioGroup: {
            flexFlow: 'row wrap',
            justifyContent: 'center',
        },
        selector: {
            height: '100%',
            display: 'flex',
            alignItems: 'flex-end'
        },
        subHeading: {
            paddingLeft: 25,            
            paddingBottom: 25,
            color: theme.palette.text.secondary
        },
        dropZoneFont: {            
            minHeight: 0,
            '& .MuiDropzoneArea-text': {
                fontSize: '1em',
            }
        },
        dropZone: {
            minHeight: 'fit-content !important' ,
            '& .MuiDropzoneArea-root' : {
                minHeight: 'fit-content !important'
            }
        },
        loadingWrapper: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px',
        }
    })
);

export function MatterSendEmailDialog(props: MatterSendEmailDialogProps) {
    
    const classes = useStyles();
    
    const applicationSettingsQuery = useApplicationSettings();
    
    const [showDiscard, setShowDiscard] = useState(false);
    
    const [isLoading, setIsLoading] = useState<boolean>(false);
    
    const { 
        maxWidth = "md",
        ...rest 
    } = props;
    
    const initialValues: NameSendEmailFormValues = {
        templateDescription: null,
        templateName: null,
        ccAddresses: null,
        emailContent: null,
        emailSubject: null,
        files: [],
        fromAddress: null,
        toAddresses: null
    };

    const required = (value: any) => {
        return value ? undefined : 'Required';
    };
    
    const GetFiles = (files: DocumentFile[]) => {
        return files.map((document: DocumentFile) => {
            return {
                file: document.file,
                name: document.name,
                guidID: document.guidID,
                status: document.status
            };
        }).filter((file) => file.status !== 2); // exclude deleted files
    };
    
    const onSubmit = (values: NameSendEmailFormValues) => {
        
        client.mutate<SendMatterEmailData, SendMatterEmailParams>({
                mutation: SendMatterEmail,
                variables: {
                    input: {
                        matterGuid: props.entityGuid,
                        emailContent: values.emailContent,
                        emailSubject: values.emailSubject,
                        emailType: EmailTemplateTypeEnum.MATTER_MARKETING_EMAIL,
                        fromEmailAddress: values.fromAddress,
                        toEmailAddresses: values.toAddresses,
                        ccEmailAddresses: values.ccAddresses,
                        emailTemplateName: values.emailTemplate?.label || null,
                        templateGuid: values.emailTemplate?.value || null,
                        files: GetFiles(values.files),
                        messageType: MessageTypeEnum.EMAIL
                    }
                },
            })
            // tslint:disable-next-line: no-any
            .then((results) => { 
                if (results.data) {
                    if (results.data.sendMatterEmail) {
                        showNotification(null, 'Successfully submitted', 'info'); 
                        props.onClose?.({}, 'backdropClick');
                    } else {
                        showNotification('Failed to send email', 'Failed to send email', 'error');
                    }
                }            
            })
            // tslint:disable-next-line:no-any
            .catch((reason: any) => { showNotification('Failed to send email', reason, 'error'); });    
    };
    
    const fetchEmailTemplateData = (emailTemplateGuid: string, form: FormApi<NameSendEmailFormValues>) => {
        
        const params: TemplateWithEmailBodyQueryParams = {
            entityGuid: props.entityGuid,
            entityType: 'Matter',
            emailTemplateGuid: emailTemplateGuid,
        };

        retrieveQueryData<TemplateWithEmailBodyQueryParams, TemplateWithEmailBodyData>(
            TemplateWithEmailBodyQuery,
            params,
            true,
            (data) => onDataRetrieved(data, form),
            // tslint:disable-next-line: no-any
            function (reason: any): void {
                showNotification(null, reason, 'error');
                setIsLoading(false);
            }
        );  
    };
    
    const onDataRetrieved = (data: TemplateWithEmailBodyData, form: FormApi<NameSendEmailFormValues>) => {
        
        form.change('fromAddress', data.templates.templateWithBody.fromAddress);
        form.change('toAddresses', data.templates.templateWithBody.toAddresses);
        form.change('ccAddresses', data.templates.templateWithBody.ccAddresses);
        form.change('emailSubject', data.templates.templateWithBody.emailSubject);
        form.change('emailContent', data.templates.templateWithBody.emailContent);
        form.change('files', data.templates.templateWithBody.files);
        
        setIsLoading(false);
        
    };
    
    const onClose = (pristine: boolean) => () => {
                
        if (!pristine) {
            setShowDiscard(true);
        } else {
            if (props.onClose) {
                props.onClose({} as any, "backdropClick");
            }
        }
    };
    
    const onDiscardChanges = (form: FormApi<NameSendEmailFormValues>) => (agree: boolean) => {
        if (agree) {
            form.reset();
            setShowDiscard(false);

            if (props.onClose) {
                props.onClose({} as any, "backdropClick");
            }
        } else {
            setShowDiscard(false);
        }
    };
        
    if (applicationSettingsQuery.loading) {
        return (
            <Dialog
                {...rest}
                maxWidth={maxWidth}
            >
                <DialogTitle
                    id="form-dialog-title"
                    className={classes.dialogTitle}
                >
                    Email Client
                </DialogTitle>
                <DialogContent dividers={true}>
                    <div
                        className={classes.loadingWrapper}
                    >
                        <RvLoader />
                    </div>
                </DialogContent>
            </Dialog>
        );
    }
    
    const CustomPopper = function (popperProps: PopperProps) {
        return <Popper {...popperProps} placement="top" />;
    };
    
    const getContent = (values: NameSendEmailFormValues) => {
        
        if (!values.emailTemplate) {
            return <div />
        }
        
        if (isLoading) {
            return (
                <Grid item={true} xs={12}>
                    <div
                        className={classes.loadingWrapper}
                    >
                        <RvLoader />
                    </div>
                </Grid>
            );
        }
        
        return (
            <>
                <Grid item={true} xs={12}>
                    <Field
                        name="fromAddress"
                        type="email"
                        subscription={{touched: true, error: true, value: true}}
                        validate={required}
                    >
                        {({ input, meta }) => (
                            <div>
                                <TextField
                                    {...input}
                                    label="From"
                                    type="email"
                                    required={true}
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    autoComplete="abcd"
                                    helperText={
                                        meta.error &&
                                        meta.touched
                                            ? meta.error
                                            : ''
                                    }
                                />
                            </div>
                        )}
                    </Field>
                </Grid>
                <Grid item={true} xs={12}>
                    <Field
                        name="toAddresses"
                        type="email"
                        subscription={{touched: true, error: true, value: true}}
                        validate={required}
                    >
                        {({ input, meta }) => (
                            <div>
                                <TextField
                                    {...input}
                                    label="To"
                                    type="email"
                                    required={true}
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    autoComplete="abcd"
                                    helperText={
                                        meta.error &&
                                        meta.touched
                                            ? meta.error
                                            : ''
                                    }
                                />
                            </div>
                        )}
                    </Field>
                </Grid>
                <Grid item={true} xs={12}>
                    <Field
                        name="ccAddresses"
                        type="email"
                        subscription={{touched: true,  error: true, value: true}}
                    >
                        {({ input, meta }) => (
                            <div>
                                <TextField
                                    {...input}
                                    label="Cc"
                                    type="email"
                                    required={undefined}
                                    className={classes.textField}
                                    error={
                                        meta.error &&
                                        meta.touched
                                    }
                                    autoComplete="abcd"
                                    helperText={
                                        meta.error &&
                                        meta.touched
                                            ? meta.error
                                            : ''
                                    }
                                />
                            </div>
                        )}
                    </Field>
                </Grid>
                <Grid item={true} xs={12}>
                    <Field
                        name="emailSubject"
                        type="text"
                        validate={required}
                        subscription={{touched: true,  error: true, value: true}}
                    >
                        {({ input, meta }) => (
                            <div>
                                <TextField
                                    {...input}
                                    label="Subject"
                                    type="text"
                                    required={true}
                                    className={classes.textField}
                                    error={
                                        meta.error &&
                                        meta.touched
                                    }
                                    autoComplete="abcd"
                                    helperText={
                                        meta.error &&
                                        meta.touched
                                            ? meta.error
                                            : ''
                                    }
                                />
                            </div>
                        )}
                    </Field>
                </Grid>
                <Grid item={true} xs={12}>                                                                
                    <Field
                        name="emailContent"
                        type="text"
                        validate={required}
                        subscription={{touched: true,  error: true, value: true}}
                    >
                        {({ input, meta }) => (
                            <EditorWrapper
                                // disabledAI={true}
                                value={input.value}
                                onEditorChange={(content: string) => {
                                    input.onChange(content);
                                }}
                                messageType={EmailTemplateMessageTypeEnum.EMAIL}
                            />
                        )}
                    </Field>
                    {/* )} */}
                </Grid>
                <Grid
                    item={true}
                    xs={12}
                    md={12}
                    // className={classes.customFieldGrid + ' ' + classes.dropZone}
                >
                    <Field
                        name="files"
                        // subscription={{touched: true, error: true, value: true}}
                    >
                        {({ input, meta }) => (
                            <DropzoneArea
                                {...input}
                                inputProps={{
                                    className: classes.dropZone
                                }}
                                // key={filesKey}
                                maxFileSize={(applicationSettingsQuery.data?.settings.applications.systemOptions.config.emailSizeLimit || 0) * 1_048_576}
                                // acceptedFiles={['.msg', '.jpg', '.jpeg', '.png', '.docx', '.zip']}
                                showFileNames={true}
                                useChipsForPreview={true}
                                filesLimit={50}
                                dropzoneText="To add a file, drag and drop or click in this box.
                                Click the 'X' on a file to delete.
                                Click the filename to replace it.
                                View a file using the links above. 
                                These links will become available once the enquiry is saved."
                                dropzoneClass={classes.dropZoneFont}
                                // tslint:disable-next-line: no-shadowed-variable
                                onChange={(files: File[]) => {
                                    let existingFiles: DocumentFile[] = [...input.value];
                                    const newFiles = files.filter((file: File) => {
                                        return !existingFiles.some((item: DocumentFile) => {
                                            return normalizeFileName(item.name) === normalizeFileName(file.name) && item.status !== 2;
                                        });
                                    });
                                    if (newFiles && newFiles.length > 0) {
                                        newFiles.forEach((file: File) => {
                                            const documentFile: DocumentFile = {
                                                guidID: null,
                                                file: file,
                                                status: 1,
                                                name: file.name,
                                                url: ''
                                            };
                                            existingFiles.push(documentFile);
                                        });  
                                        input.onChange(existingFiles);
                                    }
                                }}
                                onDelete={(file: File) => {
                                    let existingFiles: DocumentFile[] = [...input.value];
                                    // eslint-disable-next-line array-callback-return
                                    existingFiles.map((item: DocumentFile) => {
                                        if (file.name === item.name) {
                                            item.file = file;
                                            item.status = 2; // deleted
                                        }
                                    });
                                    // tslint:disable-next-line: max-line-length
                                    const filteredFiles = existingFiles.filter((item: DocumentFile) => {
                                        return item.status !== 2 || item.guidID !== null;
                                    });
                                    input.onChange(filteredFiles);
                                }}                                               
                                initialFiles={input.value && input.value.map((file: { url: string; }) => file.url)}
                            />
                        )}
                    </Field>
                </Grid>      
            </>
        );
    }
    
    return (
        <Form<NameSendEmailFormValues>
            onSubmit={onSubmit}
            initialValues={initialValues}
            initialValuesEqual={() => true}
            keepDirtyOnReinitialize={true}
            subscription={{ submitting: true, pristine: true, values: true }}
            render={({ handleSubmit, form, submitting, pristine, values}) => (
                <>
                    
                    <DialogBox
                        title="Email Client"
                        content="Are you sure you want to close the form?"
                        show={showDiscard}
                        isAgree={onDiscardChanges(form)}
                        disAgreeLabel="No"
                        agreeLabel="Yes"
                    />
                                        
                    <form onSubmit={handleSubmit}>
                        <Dialog 
                            {...rest}
                            maxWidth={maxWidth}
                            onClose={onClose(pristine)}
                        >
                            
                            <DialogTitle
                                id="form-dialog-title"
                                className={classes.dialogTitle}
                            >
                                Email Client
                            </DialogTitle>
                            <DialogContent dividers={true}>
                                <Grid container={true} spacing={1}>
                                    <Grid item={true} xs={12} md={6}>
                                        <Field
                                            name="emailTemplate"
                                            validate={required}
                                            subscription={{ touched: true, error: true, value: true }}
                                        >
                                            {({ input, meta }) => (
                                                <div>
                                                    <EmailTemplateSelector
                                                        {...input}
                                                        label="Email Template"
                                                        required={true}
                                                        value={input.value ? input.value : null}
                                                        className={classes.textField}
                                                        error={meta.error && meta.touched}
                                                        // tslint:disable-next-line: max-line-length
                                                        onSelection={(selection?: IAutoCompleteItem | IAutoCompleteItem[], name?: string) => {

                                                            // if array return
                                                            if (Array.isArray(selection) || !selection) {
                                                                return;
                                                            }

                                                            input.onChange(selection);
                                                            if (selection !== null) {
                                                                setIsLoading(true);
                                                                fetchEmailTemplateData(selection.value, form);
                                                            }
                                                        }}
                                                        onBlur={input.onBlur}
                                                        helperText={meta.error && meta.touched
                                                            ? 'Template is required'
                                                            : undefined
                                                        }
                                                        PopperComponent={CustomPopper}
                                                        // areaOfLaw={values.areaOfLaw}
                                                        emailType={EmailTemplateTypeEnum.MATTER_MARKETING_EMAIL}
                                                        disablePortal={false}
                                                        messageType={EmailTemplateMessageTypeEnum.EMAIL}
                                                        areaOfLaw=''
                                                    />
                                                </div>
                                            )}
                                        </Field>
                                    </Grid>
                                    {getContent(values)}
                                </Grid>
                            </DialogContent>
                            <DialogActions>
                                <Button 
                                    // tslint:disable-next-line: max-line-length
                                    disabled={submitting} 
                                    color="primary"
                                    type="submit"
                                    onClick={() => form.submit()}
                                >
                                    Send
                                </Button>
                                
                                <Button 
                                    type="button"
                                    onClick={onClose(pristine)}
                                    color="primary"
                                >
                                    Cancel
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </form>
                </>
            )}
        />
    );
}

export type EntityTypeType = 'Client' | 'Contact' | 'Matter' | 'Lead' | 'Staff' | 'User' | 'Task' | 'Team' | 'PendingMatter' | 'Brand' | 'General' | 'EmailTemplate' | 'Meeting';

interface TemplateWithEmailBodyData {
    templates: Templates;
}

interface Templates {
    templateWithBody: TemplateWithEmailBody;
}

interface TemplateWithEmailBody {
    id:           number;
    templateName: string | null;
    templateDescription: string | null;
    fromAddress:  string | null;
    toAddresses:  string | null;
    ccAddresses:  string | null;
    emailType:    number;
    emailStyle:   number;
    emailSubject: string | null;
    emailContent: string | null;
    files:        DocumentFile[];
}

interface TemplateWithEmailBodyQueryParams {
    entityType: EntityTypeType;
    entityGuid: string;
    emailTemplateGuid: string;
}

const TemplateWithEmailBodyQuery = gql`
    query MatterTemplateWithBody(
        $entityType: EntityTypeType,
        $entityGuid: String,
        $emailTemplateGuid: String
    ) {
        templates {
            templateWithBody (
                entityType: $entityType,
                entityGuid: $entityGuid,
                emailTemplateGuid: $emailTemplateGuid
            ) {
                templateName
                templateDescription
                toAddresses
                ccAddresses
                fromAddress
                emailType
                emailStyle
                emailSubject
                emailContent
                files {
                    entityType
                    guidID
                    name
                    url
                }
            }
        }
    }
`;

interface MatterEmailInput {
    matterGuid: string | null;
    emailContent: string | null;
    emailSubject: string | null;
    emailType: number | null;
    fromEmailAddress: string | null;
    toEmailAddresses: string | null;
    ccEmailAddresses: string | null;
    emailTemplateName: string | null;
    templateGuid: string | null;
    files: any[];
    messageType: number | null;
}

interface SendMatterEmailParams {
    input: MatterEmailInput;
}

interface SendMatterEmailData {
    sendMatterEmail: boolean;
}

const SendMatterEmail = gql`
    mutation SendMatterEmail($input: MatterEmailInput){
        sendMatterEmail(input: $input)
    }
`;